import { ref } from 'vue';
import httpService from '../services/httpService';
import type { VendasProdutos_ListaRow } from '../contracts/VendasProdutos_ListaRow';

/**
 * Venda de produtos por produto e franquia (chamado 2026-0046).
 *
 * Uma chamada só: o backend devolve a lista já filtrada pelo filtro global e
 * restrita a ordens QUITADAS. O agrupamento por país acontece aqui, e NUNCA há
 * soma entre países — a rede opera em três moedas e o dinheiro operacional não
 * guarda qual (chamado 2026-0049).
 *
 * O dinheiro chega em CENTAVOS (`so_order_items`) e sai daqui em unidades da
 * moeda, que é o que o `renderType: 'currency'` da tabela formata.
 */
const EM_CENTAVOS = ['bruto', 'desconto', 'liquido', 'ticket_medio'] as const;

const emUnidades = (linha: VendasProdutos_ListaRow): VendasProdutos_ListaRow => {
  const convertida = { ...linha };
  for (const campo of EM_CENTAVOS) convertida[campo] = (Number(linha[campo]) || 0) / 100;

  return convertida;
};

export interface GrupoPorPais {
  pais: string;
  linhas: VendasProdutos_ListaRow[];
  /** Soma do líquido DENTRO do país. Não existe total geral, de propósito. */
  totalLiquido: number;
}

export function useVendasProdutos() {
  const grupos = ref<GrupoPorPais[]>([]);
  const carregando = ref<boolean>(false);
  const erro = ref<string | null>(null);

  async function carregar(): Promise<void> {
    carregando.value = true;
    erro.value = null;

    try {
      const response = await httpService.get(
        '/index.php/relatorios/vendas_produtos/lista',
        { responseContract: 'VendasProdutos_ListaResponse' },
      ) as { success?: boolean; dados?: VendasProdutos_ListaRow[] };

      grupos.value = agrupaPorPais((Array.isArray(response?.dados) ? response.dados : []).map(emUnidades));
    } catch (e) {
      erro.value = e instanceof Error ? e.message : String(e);
      grupos.value = [];
    } finally {
      carregando.value = false;
    }
  }

  return { grupos, carregando, erro, carregar, agrupaPorPais };
}

/**
 * Agrupa por país preservando a ordem de valor que o backend já aplicou.
 *
 * Países ordenados pelo total: quem vende mais aparece primeiro. O balde '??'
 * (franquia sem país resolvível) vai para o fim — ele existe para ser visto e
 * corrigido, não para se esconder dentro do Brasil.
 */
export function agrupaPorPais(linhas: VendasProdutos_ListaRow[]): GrupoPorPais[] {
  const porPais = new Map<string, VendasProdutos_ListaRow[]>();

  for (const linha of linhas) {
    const pais = linha.pais || '??';
    const atual = porPais.get(pais);
    if (atual) {
      atual.push(linha);
    } else {
      porPais.set(pais, [linha]);
    }
  }

  const grupos: GrupoPorPais[] = [];
  for (const [pais, doPais] of porPais) {
    grupos.push({
      pais,
      linhas: doPais,
      totalLiquido: doPais.reduce((soma, l) => soma + (Number(l.liquido) || 0), 0),
    });
  }

  return grupos.sort((a, b) => {
    if (a.pais === '??') return 1;
    if (b.pais === '??') return -1;
    return b.totalLiquido - a.totalLiquido;
  });
}
